/**
 * Le blog : épisodes, Relevé hebdomadaire et notes.
 *
 * Les Relevés et les notes ci-dessous sont les EXEMPLES de la maquette, à
 * remplacer avant publication. Les épisodes viennent de `serie.ts`.
 */
import { episodePath, published } from './serie';

/** Les quatre piliers du fil rouge : le classement de chaque élément du Relevé. */
export const piliers = [
  'Sécurité opérationnelle',
  'Infrastructure et réseau',
  'IA mise au travail',
  'Open source et souveraineté',
] as const;

export type Pilier = (typeof piliers)[number];

export type Releve = {
  /** Numéro de semaine sur deux chiffres. */
  semaine: string;
  date: string;
  /** Les sujets de la semaine, en une ligne. */
  title: string;
  /** Contenu détaillé ; absent pour un Relevé archivé dont seul le titre est connu. */
  items?: { pilier: Pilier; title: string; body: string }[];
  /** Résumé chiffré quand `items` manque : « 6 éléments, 4 piliers ». */
  count?: string;
};

export const releves: Releve[] = [
  {
    semaine: '03',
    date: '2027-01-18',
    title: 'avis MCP, secrets n8n, baisse de prix',
    items: [
      {
        pilier: 'Sécurité opérationnelle',
        title: 'Avis de sécurité sur un serveur MCP populaire',
        body: 'Correctif publié, mise à jour à faire avant toute exposition réseau.',
      },
      {
        pilier: 'Infrastructure et réseau',
        title: 'n8n : nouvelle gestion des secrets',
        body: "Identifiants depuis un coffre externe. À tester pour l'épisode 6.",
      },
      {
        pilier: 'IA mise au travail',
        title: 'Baisse de prix sur le modèle intermédiaire',
        body: "Coût par exécution du pipeline recalculé dans l'épisode 3.",
      },
      {
        pilier: 'Open source et souveraineté',
        title: "Nouvelle version d'Ollama",
        body: "Meilleure gestion mémoire sur petites cartes. À vérifier pour l'épisode 9.",
      },
    ],
  },
  { semaine: '02', date: '2027-01-11', title: 'premier Relevé produit avec le pipeline', count: '6 éléments, 4 piliers' },
  { semaine: '01', date: '2027-01-04', title: 'bilan des notes de version de décembre', count: '5 éléments, 3 piliers' },
];

/** Le Relevé le plus récent qui porte son détail. */
export const latestReleve = releves.find((releve) => releve.items);

export type Note = {
  date: string;
  title: string;
  lead: string;
};

export const notes: Note[] = [
  {
    date: '2026-12-18',
    title: 'Un générateur statique plutôt que WordPress',
    lead: 'Markdown et git : moins à maintenir, moins à sécuriser, rien à mettre à jour le dimanche.',
  },
  {
    date: '2026-11-20',
    title: 'Ce que je ne publierai jamais',
    lead: "Rien de l'employeur, même anonymisé. Seuls le labo personnel et les mandats clients servent de source.",
  },
];

/* ---------------------------------------------------------------------------
   Liste unifiée, pour l'accueil et l'index du blog.
   --------------------------------------------------------------------------- */

export type PublicationKind = 'episode' | 'releve' | 'note';

export type Publication = {
  kind: PublicationKind;
  date: string;
  /** « Épisode 03 », « Le Relevé », « Note ». */
  category: string;
  title: string;
  detail?: string;
  /** Lien vers la page ; absent tant que la page n'existe pas (notes). */
  to?: string;
};

export const publications: Publication[] = [
  ...published.map(
    (episode): Publication => ({
      kind: 'episode',
      date: episode.date ?? '',
      category: `Épisode ${episode.num}`,
      title: episode.title,
      detail: episode.summary,
      to: episodePath(episode),
    }),
  ),
  ...releves.map(
    (releve): Publication => ({
      kind: 'releve',
      date: releve.date,
      category: 'Le Relevé',
      title: `Semaine ${releve.semaine} : ${releve.title}`,
      detail:
        releve.count ??
        `${String(releve.items?.length ?? 0)} éléments, ${String(new Set(releve.items?.map((item) => item.pilier)).size)} piliers`,
      to: '/blog#releve',
    }),
  ),
  ...notes.map(
    (note): Publication => ({
      kind: 'note',
      date: note.date,
      category: 'Note',
      title: note.title,
      detail: note.lead,
    }),
  ),
].sort((a, b) => b.date.localeCompare(a.date));
